import { ShopOnboardingCategory } from '@prisma/client';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import useGetOnboarding from '../../../hooks/businesses/useGetOnboarding';
import { ONBOARDING_CATEGORY_NAMES } from '../../../constants/businesses';
import Progress from '../../global/ProgressBar';
import SetupStepRow from './SetupStepRow';
import SupportRail from './SupportRail';

function SetupGuide() {
  const theme = useTheme();
  const {
    steps,
    categorySteps,
    completedSteps,
    percentCompleted,
    getCategoryProgress,
  } = useGetOnboarding();

  const isComplete = (stepId: string) =>
    completedSteps.some(step => step.id === stepId);

  return (
    <Box
      display="grid"
      gap={5}
      alignItems="start"
      maxWidth="1180px"
      gridTemplateColumns={{
        xs: 'minmax(0, 1fr)',
        lg: 'minmax(0, 1fr) 320px',
      }}>
      <Box>
        <Typography variant="h1" component="h1" mb={1}>
          Let&apos;s get you set up
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          maxWidth="620px"
          mb={3}>
          Work through these steps to start taking bookings and payments. Each
          one drops you straight into the right setting.
        </Typography>

        <Stack direction="row" alignItems="center" gap={2} mb={4.5}>
          <Progress
            progress={percentCompleted}
            includePercentage
            height="10px"
            trackColor={theme.palette.lineGrey.main}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            whiteSpace="nowrap">
            {completedSteps.length} of {steps.length} steps
          </Typography>
        </Stack>

        <Stack gap={4}>
          {Object.values(ShopOnboardingCategory).map(category => {
            const categoryStepList = categorySteps?.[category];
            if (!categoryStepList?.length) return null;
            const { completed, total } = getCategoryProgress(category);

            return (
              <Box key={category}>
                <Stack
                  direction="row"
                  alignItems="baseline"
                  justifyContent="space-between"
                  gap={2}
                  mb={1.5}>
                  <Typography variant="h3" component="h2">
                    {ONBOARDING_CATEGORY_NAMES[category]}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    whiteSpace="nowrap">
                    {completed} of {total} completed
                  </Typography>
                </Stack>
                <Box
                  borderRadius="5px"
                  overflow="hidden"
                  border={`1px solid ${theme.palette.darkerLightGrey.main}`}>
                  {categoryStepList.map(step => (
                    <SetupStepRow
                      key={step.id}
                      step={step}
                      isComplete={isComplete(step.id)}
                    />
                  ))}
                </Box>
              </Box>
            );
          })}
        </Stack>
      </Box>

      <SupportRail />
    </Box>
  );
}

export default SetupGuide;
